import type { ReactNode } from 'react';
import { formatMoney } from '@/utils/currency';
import { receiptLoyaltyDiscount } from '@/utils/receiptPromo';
import type { ReceiptData, ReceiptTemplateSettings } from '@/types';

type Props = {
  inv: Pick<ReceiptData, 'loyalty' | 'discount_amount'> | null | undefined;
  tmplSettings?: ReceiptTemplateSettings | null;
  /** classic layouts use flex justify-between; compact card uses plain totals-row */
  variant?: 'flex' | 'plain';
};

/**
 * Loyalty lines as separate totals-rows (never nest inside .totals-row —
 * that class is display:flex and causes horizontal overlap).
 */
export default function LoyaltyTotalsRows({ inv, tmplSettings, variant = 'flex' }: Props) {
  const showEarned = tmplSettings?.show_loyalty_earned !== false;
  const showBalance = tmplSettings?.show_loyalty_balance !== false;
  const earned = Math.floor(Number(inv?.loyalty?.earned) || 0);
  const redeemed = Math.floor(Number(inv?.loyalty?.redeemed) || 0);
  const loyaltyAmt = receiptLoyaltyDiscount(inv);
  const balance = inv?.loyalty?.balance;

  const rowClass =
    variant === 'flex' ? 'totals-row flex justify-between' : 'totals-row';

  const nodes: ReactNode[] = [];

  if (loyaltyAmt > 0 || redeemed > 0) {
    nodes.push(
      <div key="loyalty-redeem" className={rowClass}>
        <span>
          Loyalty points redeem
          {redeemed > 0 ? ` (${redeemed} pts)` : ''}
        </span>
        <span>− {formatMoney(loyaltyAmt || 0)}</span>
      </div>,
    );
  }

  if (showEarned && earned > 0) {
    nodes.push(
      <div key="loyalty-earned" className={rowClass}>
        <span>Points earned</span>
        <span>{earned}</span>
      </div>,
    );
  }

  if (showBalance && balance != null) {
    nodes.push(
      <div key="loyalty-balance" className={rowClass}>
        <span>Points balance</span>
        <span>{Math.floor(Number(balance) || 0)}</span>
      </div>,
    );
  }

  if (!nodes.length) return null;
  return <>{nodes}</>;
}
